/* eslint-disable indent */
"use client";

import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Play, Volume2 } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useRouter } from "next/router";
import { toast } from "sonner";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Skeleton } from "@/components/ui/skeleton";
import { useHandleADetailerClick, useHandleAudioClick, useHandleBGRemovalClick, useHandleEditClick, useHandleFaceSwapClick, useHandleInpaintClick, useHandleQuickEditClick, useHandleUpscalerModalClick } from "@/lib/hooks";
import { cn } from "@/lib/utils";
import SocialShare from "components/atoms/SocialShare";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import EligibleCompetitionsModal from "components/molecules/EligibleCompetitionsModal";
import ShareModal from "components/molecules/ShareModal";
// Actions and Context
import { deleteImage, getCommentsByImage, getMonsterStatsByImageId, getNPCStatsByImageId, onToggleNSFW, onToggleVisibility, voteOnImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, GameEntityDTO, ICommentDTO, ImageDTO, RequestType } from "types";
import ImageTabs from "../ImageTabs";

// Defer non-critical UI components
const ControlsContainer = dynamic(() => import("components/molecules/ControlsContainer"), {
  ssr: false,
  loading: () => <div className="w-full flex justify-center my-2">
                <Skeleton className="h-10 w-full max-w-md rounded-full" />
            </div>
});
const ReactPlayer = dynamic(() => import("react-player"), {
  ssr: false,
  loading: () => <div className="w-full aspect-video bg-muted/20 animate-pulse flex items-center justify-center">
            <Play className="w-12 h-12 text-muted-foreground/30" />
        </div>
});
const ReactAudioPlayer = dynamic(() => import("react-audio-player"), {
  ssr: false,
  loading: () => <div className="w-full h-[60px] bg-muted/20 animate-pulse flex items-center justify-center">
            <Volume2 className="w-8 h-8 text-muted-foreground/30" />
        </div>
});
interface ImagePageProps {
  imageDto: ImageDTO;
  metaTitle: string;
  metaDescription: string;
}
export const ImagePage: FC<ImagePageProps> = ({
  imageDto,
  metaTitle,
  metaDescription
}) => {
  const router = useRouter();
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [originalImage, setOriginalImage] = useState<ImageDTO>(imageDto);
  const [selectedImage, setSelectedImage] = useState<ImageDTO>(imageDto);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showEligibleCompetitionsModal, setShowEligibleCompetitionsModal] = useState(false);
  const [comments, setComments] = useState<ICommentDTO[]>([]);
  const [stats, setStats] = useState<GameEntityDTO | null>(null);
  const [imageDimensions] = useState(() => {
    if (imageDto.imageMetadata?.npcFieldsMap?.["Size"]) {
      const [width, height] = imageDto.imageMetadata.npcFieldsMap["Size"].split("x");
      return {
        width: parseInt(width),
        height: parseInt(height)
      };
    }
    // Default to common image sizes
    return {
      width: 1024,
      height: 1024
    };
  });
  const [showLabels, setShowLabels] = useState<boolean>(false);

  // Optimization: Only update on explicit state changes
  useEffect(() => {
    setOriginalImage(imageDto);
    setSelectedImage(imageDto);
  }, [imageDto]);

  // Simple resize handler with a single state update
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => {
      setShowLabels(window.innerWidth > 700);
    };

    // Set initial value
    handleResize();

    // Use passive event listener for better performance
    window.addEventListener("resize", handleResize, {
      passive: true
    });
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const handleUpscaleClick = useHandleUpscalerModalClick(imageDto.id);
  const handleAudioClick = useHandleAudioClick(imageDto.id);
  const handleFaceSwapClick = useHandleFaceSwapClick(imageDto.id);
  const handleInpaintClick = useHandleInpaintClick(imageDto.id);
  const handleBGRemovalClick = useHandleBGRemovalClick(imageDto.id);
  const handleADetailerClick = useHandleADetailerClick(imageDto.id);
  const handleEditClick = useHandleEditClick(imageDto.id);
  const handleQuickEditClick = useHandleQuickEditClick(imageDto.id);
  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsShareModalOpen(true);
  };
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };
  const handleCompetitionsClick = () => {
    setShowEligibleCompetitionsModal(true);
  };
  const handleCloseEligibleCompetitionsModal = () => {
    setShowEligibleCompetitionsModal(false);
  };
  const prepareForDelete = () => {
    setShowDeleteConfirmation(true);
  };
  const handleConfirmDelete = async () => {
    try {
      await deleteImage(selectedImage.id);
      if (selectedImage.id === originalImage.id) {
        router.push("/");
      } else {
        const updatedChildImages = (originalImage.childImages || []).filter(child => child.id !== selectedImage.id);
        const updatedOriginalImage = {
          ...originalImage,
          childImages: updatedChildImages
        };
        setOriginalImage(updatedOriginalImage);
        setSelectedImage(updatedOriginalImage);
      }
      setShowDeleteConfirmation(false);
    } catch (error: any) {
      console.error("Failed to delete image", error);
      setShowDeleteConfirmation(false);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };
  const handleToggle = async (imageId: string) => {
    const currentVisibility = selectedImage.public;
    const newVisibility = !currentVisibility;
    try {
      const result = await onToggleVisibility(imageId, newVisibility);
      if (result.error) {
        toast.error("Failed to update visibility status: " + result.error);
        return;
      }
      setSelectedImage(prev => ({
        ...prev,
        public: newVisibility
      }));
      originalImage.childImages?.forEach(child => {
        if (child.id === imageId) {
          child.public = newVisibility;
        }
      });
    } catch (error: any) {
      console.error("Failed to update visibility status.", error);
    }
  };

  // Fetch comments in a deferred way (after main content loads)
  useEffect(() => {
    if (!selectedImage) return;
    let isMounted = true;
    const fetchComments = async () => {
      try {
        const response = await getCommentsByImage(selectedImage.id, "top");
        if (!isMounted) return;
        if (response.comments) {
          setComments(response.comments);
        } else if (response.error) {
          console.error("Failed to fetch comments:", response.error);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching comments:", error);
        }
      }
    };
    const timer = setTimeout(() => {
      fetchComments();
    }, 1000);
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [selectedImage, selectedImage?.id]);
  const handleImageClick = (image: ImageDTO) => {
    setSelectedImage(image);
  };

  // Defer stats fetching even further since it's not critical
  useEffect(() => {
    if (!selectedImage) return;
    let isMounted = true;
    const fetchStats = async () => {
      try {
        let response = null;
        switch (selectedImage.imageMetadata.requestType) {
          case RequestType.MONSTER:
            response = await getMonsterStatsByImageId(selectedImage.id);
            break;
          case RequestType.NPC:
            response = await getNPCStatsByImageId(selectedImage.id);
            break;
          default:
            break;
        }
        if (!isMounted) return;
        setStats(response || null);
      } catch (error: any) {
        if (isMounted) {
          console.error("Failed to fetch stats", error);
        }
      }
    };

    // Use requestAnimationFrame to defer non-critical data fetching
    const timer = setTimeout(() => {
      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => {
          fetchStats();
        });
      } else {
        fetchStats();
      }
    }, 2000);
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [selectedImage, selectedImage?.id, selectedImage?.imageMetadata.requestType]);
  const handleVote = async (imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    const newVoteStatus = !selectedImage.votedByUser;
    try {
      await voteOnImage(imageId, newVoteStatus);
      setSelectedImage(prev => ({
        ...prev,
        votedByUser: newVoteStatus,
        voteCount: prev.voteCount + (newVoteStatus ? 1 : -1)
      }));
      originalImage.childImages?.forEach(child => {
        if (child.id === imageId) {
          child.votedByUser = newVoteStatus;
          child.voteCount += newVoteStatus ? 1 : -1;
        }
      });
    } catch (error: any) {
      console.error("Failed to update vote status.", error);
    }
  };
  const isUserImage = (imageUsername: string) => state.username === imageUsername;

  // Memoize calculated values
  const isVideo = useMemo(() => selectedImage?.image.endsWith(".mp4") || false, [selectedImage?.image]);
  const isAudio = useMemo(() => selectedImage?.image.endsWith(".mp3") || false, [selectedImage?.image]);

  // Use a simplified aspect ratio calculation
  const aspectRatio = useMemo(() => {
    if (isVideo) {
      // Use video metadata if available
      return selectedImage.imageMetadata.npcFieldsMap?.["Size"] ? parseFloat(selectedImage.imageMetadata.npcFieldsMap["Size"].split(":")[0]) / parseFloat(selectedImage.imageMetadata.npcFieldsMap["Size"].split(":")[1]) : 16 / 9;
    }
    // Just use the image dimensions or fallback to 1:1
    return imageDimensions.width && imageDimensions.height ? imageDimensions.width / imageDimensions.height : 1;
  }, [isVideo, selectedImage?.imageMetadata.npcFieldsMap, imageDimensions]);

  // Memoize carousel images array
  const carouselImages = useMemo(() => [...(originalImage.parentImage ? [originalImage.parentImage] : []), originalImage, ...(originalImage.childImages || [])], [originalImage]);
  const handleToggleNSFW = async (imageId: string) => {
    try {
      await onToggleNSFW(imageId);
      setSelectedImage(prev => ({
        ...prev,
        public: !prev.public
      }));
      originalImage.childImages?.forEach(child => {
        if (child.id === imageId) {
          child.public = !child.public;
        }
      });
    } catch (error: any) {
      console.error("Failed to update NSFW status.", error);
    }
  };

  // Optimized image rendering with immediate display
  const renderImage = useCallback(() => {
    if (!selectedImage) {
      return <div className="w-full aspect-square">
                    <Skeleton className="w-full h-full" />
                </div>;
    }
    if (isVideo) {
      return <div className="w-full rounded-lg overflow-hidden shadow-lg bg-black">
                    <AspectRatio ratio={aspectRatio}>
                        <ReactPlayer url={selectedImage.image} width="100%" height="100%" playing controls loop muted playsinline className="absolute inset-0" />
                    </AspectRatio>
                </div>;
    }
    if (isAudio) {
      return <div className="w-full h-24 flex items-center justify-center bg-slate-900/20 rounded-lg p-4 shadow-inner">
                    <ReactAudioPlayer src={selectedImage.image} autoPlay controls loop volume={0.5} className="w-full" />
                </div>;
    }

    // Use WebP format if available for better performance
    // Include specific width and height to avoid layout shift
    return <div className="relative w-full rounded-lg overflow-hidden shadow-2xl">
                <Image src={selectedImage.webpImage || selectedImage.image} alt={selectedImage.imageMetadata.description || "Artwork"} width={imageDimensions.width || 800} height={imageDimensions.height || 800} loading="eager" sizes="(max-width: 640px) 100vw, (max-width: 768px) 85vw, (max-width: 1024px) 65vw, (max-width: 1280px) 50vw, 800px" quality={80} priority={true} className="w-full h-auto object-contain rounded" />
            </div>;
  }, [selectedImage, isVideo, isAudio, aspectRatio, imageDimensions]);

  // Memoize controls component
  const controlsComponent = useMemo(() => <ControlsContainer imageInfo={selectedImage} handleToggle={handleToggle} handleShareClick={handleShareClick} handleCompetitionsClick={handleCompetitionsClick} handleEditClick={handleEditClick} handleADetailerClick={handleADetailerClick} handleFaceSwapClick={handleFaceSwapClick} handleBGRemovalClick={handleBGRemovalClick} handleInpaintClick={handleInpaintClick} handleUpscaleClick={handleUpscaleClick} handleAudioClick={handleAudioClick} handleQuickEditClick={handleQuickEditClick} prepareForDelete={prepareForDelete} handleVote={handleVote} isUserImage={isUserImage} showLabel={showLabels} handleToggleNSFW={handleToggleNSFW} isAdmin={state.user?.admin || false} />,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [selectedImage, showLabels, isUserImage]);

  // Memoize social share
  const socialShareComponent = useMemo(() => <div className="flex flex-wrap justify-center gap-2 mt-4">
                <SocialShare image={selectedImage} description={metaDescription} />
            </div>, [selectedImage, metaDescription]);

  // Memoize tabs - load after main content
  const imageTabs = useMemo(() => <ImageTabs stats={stats} comments={comments} selectedImage={selectedImage} />, [selectedImage, stats, comments]);
  return <main className="min-h-screen bg-slate-950 text-slate-200" data-sentry-component="ImagePage" data-sentry-source-file="index.tsx">
            <div className="w-full max-w-7xl mx-auto px-2 py-4 md:py-6 md:px-4 space-y-4 md:space-y-6">
                {/* Hero Header */}
                <header>
                    <h1 className="text-lg md:text-xl text-center text-white font-medium tracking-tight">
                        {selectedImage.imageMetadata?.name || metaTitle}
                    </h1>
                </header>

                <div className="flex flex-col lg:flex-row gap-2 md:gap-6">
                    {/* Main Content Column - Critical path */}
                    <div className="w-full lg:w-2/3 space-y-4 md:space-y-4">
                        {/* Main Media Display - CRITICAL PATH */}
                        <section className="overflow-hidden rounded-xl bg-slate-900/90 border border-slate-800/50 shadow-2xl">
                            {renderImage()}
                        </section>

                        {/* Action Controls - Most important actions */}
                        <section className="rounded-2xl bg-slate-900/80 p-0 shadow-xl">
                            {controlsComponent}
                        </section>

                        {/* Mobile Metadata - Only visible on mobile */}
                        <div className="lg:hidden space-y-4">{imageTabs}</div>

                        {/* Related Images Carousel */}
                        <section className="overflow-hidden">
                            <div className="w-full mt-4">
                                <h3 className="text-lg font-semibold mb-3 text-sky-400/90">
                                    Related Images
                                </h3>
                                <Carousel opts={{
                align: "start",
                loop: carouselImages.length > 4
              }} className="w-full" data-sentry-element="Carousel" data-sentry-source-file="index.tsx">
                                    <CarouselContent data-sentry-element="CarouselContent" data-sentry-source-file="index.tsx">
                                        {carouselImages.map(image => <CarouselItem key={image.id} className="basis-1/3 md:basis-1/4 lg:basis-1/5">
                                                <div className="relative p-2 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:scale-105" onClick={() => handleImageClick(image)}>
                                                    <Card className={cn("overflow-hidden !border-2 !h-28", selectedImage.id === image.id ? "!border-sky-500 !ring-2 !ring-sky-500/50" : "!border-transparent hover:!border-sky-500/30")}>
                                                        {image.image.endsWith("mp4") ? <div className="w-full h-full flex items-center justify-center bg-black/20 rounded-md">
                                                                <Play className="w-6 h-6 text-white border-2 border-white rounded-full p-1" />
                                                            </div> : image.image.endsWith("mp3") ? <div className="w-full h-full flex items-center justify-center bg-slate-800 rounded-md">
                                                                <Volume2 className="w-8 h-8 text-gray-400" />
                                                            </div> : <div className={`relative cursor-pointer overflow-hidden rounded-md transition-all duration-200 hover:scale-105 ${image.id === selectedImage.id ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`} onClick={() => handleImageClick(image)}>
                                                                <Image src={image.webpImage || image.image} alt={image.imageMetadata.description || "Image"} width={100} height={100} className="object-cover w-full h-full" loading="lazy" />
                                                            </div>}
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                                                        <div className="absolute bottom-2 left-2 right-2">
                                                            <p className="text-xs text-white/90 text-ellipsis line-clamp-1 font-medium p-1">
                                                                {image.imageMetadata.name || image.imageMetadata.description || "Untitled"}
                                                            </p>
                                                        </div>
                                                    </Card>
                                                </div>
                                            </CarouselItem>)}
                                    </CarouselContent>
                                    <CarouselPrevious className="left-1 !bg-slate-900/80 hover:!bg-slate-800/80 !border-slate-700/50 text-white" data-sentry-element="CarouselPrevious" data-sentry-source-file="index.tsx" />
                                    <CarouselNext className="right-1 !bg-slate-900/80 hover:!bg-slate-800/80 !border-slate-700/50 text-white" data-sentry-element="CarouselNext" data-sentry-source-file="index.tsx" />
                                </Carousel>
                            </div>
                        </section>

                        {/* Social Sharing */}
                        <section className="flex justify-center py-2">
                            {socialShareComponent}
                        </section>
                    </div>

                    {/* Sidebar - Only visible on desktop */}
                    <div className="hidden lg:block w-1/3 space-y-6">
                        <div className="sticky top-4 space-y-6">
                            {imageTabs}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <ShareModal isOpen={isShareModalOpen} image={selectedImage} onClose={handleCloseShareModal} data-sentry-element="ShareModal" data-sentry-source-file="index.tsx" />

            <EligibleCompetitionsModal isOpen={showEligibleCompetitionsModal} onCancel={handleCloseEligibleCompetitionsModal} imageId={selectedImage.id} data-sentry-element="EligibleCompetitionsModal" data-sentry-source-file="index.tsx" />

            <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} text="image" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
        </main>;
};